<template>
  <div class="object-configuration">
    <ul class="object-configuration__tabs" :style="ulStyle">
      <li class="object-configuration__tab" :class="{ active: isShowEllipticalTab }"
          @click="changeMode(tabs.ELLIPTICAL)" :style="firstLiStyle">
                <span class="object-configuration__tab--inner" :style="spanStyle"> {{ $t.ellipticalOrbitObject }}</span>
        <div :style="borderStyle"></div>
      </li>
      <li class="object-configuration__tab" :class="{ active: isShowHyperbolicTab }"
          @click="changeMode(tabs.HYPERBOLIC)" :style="liStyle">
                <span class="object-configuration__tab--inner" :style="spanStyle"> {{ $t.hyperbolicOrbitObject }}
                    <Tooltip position="bottom" :tooltipText="$t.optionToSetAndToDisplayObjectsWithParabolicAndHyperbolic" :style="tooltipStyle">
                          <i class="icon-info" :style="iconStyle"/>
                    </Tooltip>
        </span>
      </li>
    </ul>
    <div class="popup-content">
      <div v-show="isShowEllipticalTab" class="select-list">
        <div class="synthetic-object">
          <p class="synthetic-object__title">{{ $t.addUserDefinedObject }}</p>
          <div class="synthetic-object__wrapper">
            <div class="synthetic-object__column synthetic-object__column--parameters">
              <div class="synthetic-object__subtitle">
                <p>
                  {{ $t.provideObjectValues }}
                </p>
              </div>
              <form novalidate class="synthetic-object__inputs-wrapper">
                <template v-for="input in ellipticalInputsList">
                  <CustomInput :key="input.id" v-if="!input.isAlwaysVisible" :inputData="input"
                               :value="ellipticalParameters[input.id]" v-model="ellipticalParameters[input.id]"
                               @input="updateInputValue($event, input.id, input.type)"
                               @dateChange="updateDateValue($event, input.id)" :loading="loading"
                               :ref="input.id" :isError="errorsInEllipticalTab[input.id]"
                               :isPerihelionAphelionError="errorsInEllipticalTab['perihelionAphelion']"
                               :disabled="isInputDisabled(input.id)"/>
                </template>
              </form>
            </div>
            <div class="synthetic-object__column synthetic-object__column--search">
              <div class="synthetic-object__subtitle">
                                <span :style="spanStyle">
                                    {{ $t.copyExistingObject }}
                                    <Tooltip position="right" :tooltipText="$t.objectsColorInList"
                                             :style="tooltipStyle">
                                        <i class="icon-info" :style="iconStyle"/>
                                    </Tooltip>
                                </span>
              </div>
              <div class="synthetic-object__search search">
                                <span class="search__wrapper">
                                    <i class="search__icon icon-search"/>
                                    <input class="search__input" ref="objectInput" type="text" v-model="name"
                                           :placeholder="$t.startTyping" :class="{ loading: loadingAsteroidsList }"
                                           @keyup="findAsteroidByPhrase($event)"/>
                                    <div class="total-results-search">
                                      <div :style="totalResultsBorderStyle"></div>
                                      <span>{{ availableAsteroidsLabel }}</span>
                                    </div>
                                </span>
                <ul ref="availableList" class="search__list" @scroll.passive="onAvailableScroll">
                  <li class="search__spacer" :style="{ height: `${topSpacerHeight}px` }"></li>
                  <li class="search__item" :class="{
                                        'search__item--active': isAvailableSelected(item),
                                    }" v-for="(item, index) of visibleAvailableAsteroids"
                      :style="{ color: getItemColor(item) }" :key="`visible_${index}_${item.id || item.name}`"
                      @click="onAvailableSelect(item)">
                    {{ displayName(item) }}
                  </li>
                  <li class="search__spacer" :style="{ height: `${bottomSpacerHeight}px` }"></li>
                </ul>
                <div class="search__action">
                  <button type="button" class="search__button secondary" :class="{
                                        disabled: !ellipticalAvailableSelected || loading,
                                        loading: loading,
                                    }" :disabled="!ellipticalAvailableSelected || loading"
                          @click="handleCopyObjectValues()">
                                        <span class="search__button-content" :class="{
                                            'search__button-content--hidden': loading,
                                        }">&lt; {{ $t.copyValues }}</span>
                    <span v-if="loading" class="search__button-content--loading">{{
                        $t.copyInProgress
                      }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-show="isShowHyperbolicTab" class="select-list">
        <div class="synthetic-object">
          <p class="synthetic-object__title">{{ $t.addUserDefinedObject }}</p>
          <div class="synthetic-object__wrapper">
            <div class="synthetic-object__column synthetic-object__column--parameters">
              <div class="synthetic-object__subtitle">
                <p>
                  {{ $t.provideObjectValues }}
                </p>
              </div>
              <form novalidate class="synthetic-object__inputs-wrapper">
                <template v-for="input in hyperbolicInputsList">
                  <CustomInput :key="input.id" v-if="!input.isAlwaysVisible" :inputData="input"
                               :value="hyperbolicParameters[input.id]" v-model="hyperbolicParameters[input.id]"
                               @input="updateInputValue($event, input.id, input.type)"
                               @dateChange="updateDateValue($event, input.id)" :loading="loading"
                               :ref="input.id" :isError="errorsInHyperbolicTab[input.id]"
                               :isPerihelionAphelionError="errorsInHyperbolicTab['perihelionAphelion']"
                               />
                </template>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="popup-footer">
      <button type="button" class="synthetic-object__button link"
              :class="{ disabled: loading || !isAnyParameterProvided }" @click="clearAllParameters()"
              :disabled="loading || !isAnyParameterProvided">
        {{ $t.clearAll }}
      </button>
      <button type="button" class="synthetic-object__button cancel" :class="{ disabled: loading }"
              @click="cancel()" :disabled="loading">
        {{ $t.cancel }}
      </button>
      <button type="button" class="synthetic-object__button primary" :class="{
                disabled: isSaveDisabled,
            }" @click="saveObject()" :disabled="isSaveDisabled">
        {{ $t.saveObject }}
      </button>
    </div>
  </div>
</template>

<script>
import Tooltip from '@/components/common/Tooltip.vue'
import CustomInput from "@/components/common/CustomInput.vue";
import ObjectsService from "@/services/objects.service";
import PopupService from "@/services/popup.service";
import SynodicCalculationService from "@/services/synodic-calculation.service";
import SynodicChartService from "@/services/synodic-chart.service";
import SyntheticObjectsService from "@/services/synthetic-objects.service";
import UtilsService from "@/services/utils.service";
import VisualisationService from "@/services/visualisation.service";
import config from '@/constants/config'
import moment from 'moment';
import isEqual from "lodash/isEqual";
import SpacekitService from "@/services/spacekit.service";

export default {
  name: "PopupTabsObjectConfiguration",
  components: {
    CustomInput,
    Tooltip,
  },
  props: {
    config: Object,
  },
  data() {
    return {
      name: "",
      typingDelay: null,
      ellipticalAvailableSelected: null,
      ellipticalParameters: {},
      hyperbolicParameters: {},
      errorsInEllipticalTab: {},
      errorsInHyperbolicTab: {},
      disabledFields: {
        rightAscension: false,
      },
      ulStyle: {
        // 'flex' will evenly space the 'li' elements
        display: 'flex',
        justifyContent: 'space-between',
        textAlign: 'center',
        borderBottom: '1px solid $bg-lightest'
      },
      liStyle: {
        // 'li' elements will take up to 50% of the width and centers their contents using flex
        width: '50%',
        padding: '15px 0',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative'
      },
      firstLiStyle: {
        // inherits from 'listyle' but does not include border-right allowing 'borderStyle' div to be added separately
        width: '50%',
        padding: '15px 0',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative'
      },
      // adds an absolutely positioned div to simulate a border with a height of 16px.
      borderStyle: {
        position: 'absolute',
        right: '0',
        height: '16px',
        width: '1px',
        backgroundColor: '#3D4659',
        top: '50%',
        transform: 'translateY(-50%)'
      },
      // uses inline-flex and whiteSpace: nowrap to ensure contents stay on one line.
      spanStyle: {
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        justifyContent: 'start'
      },
      // ensures the icon has a transparent background with backgroundColor: 'transparent'.
      iconStyle: {
        color: '#0F84DD',
        cursor: 'pointer',
        lineHeight: 'inherit',
        verticalAlign: 'middle',
        backgroundColor: 'transparent'
      },
      tooltipStyle: {
        whiteSpace: 'normal',  // Allow text to wrap
        color: '#fff',
        padding: '5px 5px',
        borderRadius: '4px',
        maxWidth: '200px'
      },
      totalResultsBorderStyle: {
        height: '16px',
        width: '1px',
        backgroundColor: '#b4b4b4',
        margin: '0 5px',
      },
      tabs: {
        ELLIPTICAL: 'ELLIPTICAL',
        HYPERBOLIC: 'HYPERBOLIC',
      },
      currentTab: '',
      availableScrollTop: 0,
      availableRowHeight: 22,
      availableOverscanRows: 20,
    };
  },
  computed: {
    availableAsteroidsList() {
      return ObjectsService.getAvailableAsteroidsList();
    },

    loadingAsteroidsList() {
      return ObjectsService.getLoadingAsteroidsList();
    },

    availableAsteroidsTotal() {
      return ObjectsService.getAvailableAsteroidsTotal();
    },

    availableAsteroidsHasMore() {
      return ObjectsService.getAvailableAsteroidsHasMore();
    },

    availableAsteroidsLabel() {
      const total = Number(this.availableAsteroidsTotal || 0);
      const unitLabel = total === 1 ? this.$t.result : this.$t.results;
      return `${total} ${unitLabel.toLowerCase()}`;
    },

    virtualStartIndex() {
      const start = Math.floor(this.availableScrollTop / this.availableRowHeight) - this.availableOverscanRows;
      return Math.max(0, start);
    },

    virtualEndIndex() {
      const rowsInViewport = 50;
      const end = this.virtualStartIndex + rowsInViewport + this.availableOverscanRows * 2;
      return Math.min(this.availableAsteroidsList.length, end);
    },

    visibleAvailableAsteroids() {
      return this.availableAsteroidsList.slice(this.virtualStartIndex, this.virtualEndIndex);
    },

    topSpacerHeight() {
      return this.virtualStartIndex * this.availableRowHeight;
    },

    bottomSpacerHeight() {
      const hiddenRows = this.availableAsteroidsList.length - this.virtualEndIndex;
      return Math.max(0, hiddenRows * this.availableRowHeight);
    },

    ellipticalInputsList() {
      return SyntheticObjectsService.getEllipticalInputsList();
    },

    hyperbolicInputsList() {
      return SyntheticObjectsService.getHyperbolicInputsList();
    },

    isShowEllipticalTab() {
      return this.currentTab === this.tabs.ELLIPTICAL;
    },

    isShowHyperbolicTab() {
      return this.currentTab === this.tabs.HYPERBOLIC;
    },

    isParametersProvided() {
      if (this.isShowEllipticalTab) {
        return UtilsService.checkObjectPropertiesHasValue({
          ...this.ellipticalParameters,
        });
      } else {
        return UtilsService.checkObjectPropertiesHasValue({
          ...this.hyperbolicParameters,
        });
      }
    },

    isFormError() {
      return this.isShowEllipticalTab ? Object.values(this.errorsInEllipticalTab).some((error) => error) : Object.values(this.errorsInHyperbolicTab).some((error) => error);
    },

    isSaveDisabled() {
      return this.loading
        || !this.isParametersProvided
        || this.isFormError;
    },

    isAnyParameterProvided() {
      if (this.isShowEllipticalTab) {
        return UtilsService.checkAnyObjectPropertyHasValue({
          ...this.ellipticalParameters,
        })
      } else {
        return UtilsService.checkAnyObjectPropertyHasValue({
          ...this.hyperbolicParameters,
        })
      }
    },

    isEditMode() {
      return this.config.editedObjectIndex || this.config.editedObjectIndex === 0;
    },

    loading() {
      return VisualisationService.loadingObjectsEphemerides().length;
    },

    currentTool() {
      return VisualisationService.getTool();
    },
  },
  watch: {
    ellipticalParameters: {
      handler: 'checkDisableOrbitalFields',
      deep: true,
    },
    availableAsteroidsList() {
      const element = this.$refs.availableList;
      if (!element) return;
      this.availableScrollTop = element.scrollTop || 0;
    },
  },
  methods: {

    changeMode(mode) {
      this.currentTab = mode;
    },

    isEllipticalType(type) {
      return type === this.tabs.ELLIPTICAL;
    },

    isInputDisabled(inputId) {
      if (inputId === 'rightAscension') {
        return this.disabledFields.rightAscension;
      }
      return false;
    },

    fillEditedObjectParameters() {
      if (this.ellipticalObjectsList().length > 0 && this.config.objectType === this.tabs.ELLIPTICAL) {
        let objectsList = UtilsService.deepCopy(this.ellipticalObjectsList());
        let object = objectsList.find((object) => isEqual(object, this.config.selectedObject));
        if (object && object.referenceEpoch) {
          object.referenceEpoch = this.julianToDateObject(object.referenceEpoch);
        }
        if (object && object.pericentrePassage) {
          object.pericentrePassage = this.julianToDateObject(object.pericentrePassage);
        }
        Object.keys(this.ellipticalParameters).forEach((parameterKey) =>
            this.setEllipticalParameter(parameterKey, object[parameterKey])
        );
        this.checkDisableOrbitalFields();
      }
      if (this.hyperbolicObjectsList().length > 0 && this.config.objectType === this.tabs.HYPERBOLIC) {
        let objectsList = UtilsService.deepCopy(this.hyperbolicObjectsList());
        let object = objectsList.find((object) => isEqual(object, this.config.selectedObject));
        if (object && object.referenceEpoch) {
          object.referenceEpoch = this.julianToDateObject(object.referenceEpoch);
        }
        if (object && object.pericentrePassage) {
          object.pericentrePassage = this.julianToDateObject(object.pericentrePassage);
        }
        Object.keys(this.hyperbolicParameters).forEach((parameterKey) =>
            this.setHyperbolicParameter(parameterKey, object[parameterKey])
        );
      }
    },

    resetParameters() {
      if (this.isShowEllipticalTab)
        Object.keys(this.ellipticalParameters).forEach((parameterKey) => this.setEllipticalParameter(parameterKey, null));
      if (this.isShowHyperbolicTab)
        Object.keys(this.hyperbolicParameters).forEach((parameterKey) => this.setHyperbolicParameter(parameterKey, null));
    },

    setParameters() {
      this.setEllipticalParameters();
      this.setHyperbolicParameters();
    },

    updateInputValue(event, parameterId, dataType) {
      if (dataType === 'date') {
        return;
      }
      if (this.isShowEllipticalTab) {
        this.setEllipticalParameter(parameterId, event.target.value);
      }
      if (this.isShowHyperbolicTab) {
        this.setHyperbolicParameter(parameterId, event.target.value);
      }
    },

    updateDateValue(event, parameterId) {
      if (this.isShowEllipticalTab) {
        this.setEllipticalParameter(parameterId, event);
      }
      if (this.isShowHyperbolicTab) {
        this.setHyperbolicParameter(parameterId, event);
      }
    },

    checkDisableOrbitalFields() {
      const i = parseFloat(this.ellipticalParameters.inclination);

      const iIsValid = !isNaN(i);

      if (iIsValid && i === 0) {
        this.disabledFields.rightAscension = true;
        this.ellipticalParameters.rightAscension = 0;
        return;
      }
      
      this.disabledFields.rightAscension = false;
    },

    getItemColor(item) {
      const settings = config.visualisation.settings;
      if (item.code === "non-NEO") {
        return settings.nonNEAcolor;
      } else if (item.sourceType.name === "NEOCP") {
        return settings.NEOCPcolor;
      }
    },

    displayName(item) {
      return ObjectsService.objectNumberAndName(item);
    },

    findAsteroidByPhrase($event) {
      this.ellipticalAvailableSelected = null;
      clearTimeout(this.typingDelay);
      this.typingDelay = setTimeout(() => {
        const phrase = $event.target.value;
        ObjectsService.findAsteroidByPhrase(phrase, false, 'ovt', { includeFilters: false });
        const element = this.$refs.availableList;
        if (element) {
          element.scrollTop = 0;
        }
        this.availableScrollTop = 0;
      }, 400);
    },

    onAvailableScroll() {
      const element = this.$refs.availableList;
      if (!element) return;
      this.availableScrollTop = element.scrollTop || 0;

      const loadMoreOffset = 200;
      const isNearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - loadMoreOffset;
      if (isNearBottom && this.availableAsteroidsHasMore && !this.loadingAsteroidsList) {
        ObjectsService.loadMoreAvailableAsteroids();
      }
    },

    onAvailableSelect(item) {
      if (this.ellipticalInputsList) {
        this.ellipticalAvailableSelected = this.isAvailableSelected(item) || item.sourceType.name === "NEOCP" ? null : item;
      }
    },

    isAvailableSelected(item) {
      if (this.ellipticalInputsList) {
        return !this.ellipticalAvailableSelected ? false : this.ellipticalAvailableSelected.name === item.name;
      }
    },

    async handleCopyObjectValues() {
      if (!this.ellipticalAvailableSelected || this.loading) {
        return;
      }
      const designator = await SyntheticObjectsService.getObjectDesignator(this.ellipticalAvailableSelected);
      const objectInformation = await SyntheticObjectsService.getRealObjectParameters(designator);
      const errorObject = objectInformation.errors.length ? objectInformation.errors[0] : null;
      const objectData = objectInformation.data;
      const objectParameters = objectData.length ? objectData[0] : null;
      objectParameters && (objectParameters.catalogueNumber = objectParameters.catalogueNumber ?? objectParameters.designator);
      this.stopLoading(designator);
      if (errorObject || !objectParameters) {
        this.resetParameters();
        errorObject && this.showPopupInfo(errorObject.message + " " + errorObject.name, "error");
        return;
      }
      this.updateSelectedObjectParameters(objectParameters);
      this.checkDisableOrbitalFields();
    },

    julianToDateObject(julian) {
      const julianDate = julian < 240000.5 ? julian + 2400000.5 : julian;
      // extracted from julian-date library because of missing roundings
      return new Date(Math.ceil(((julianDate + 0.5 - 2440588).toFixed(8) * 1000 * 60 * 60 * 24)));
    },

    updateSelectedObjectParameters(objectParameters) {
      const updatedParameters = SyntheticObjectsService.getUpdatedObjectParameters(objectParameters);
      updatedParameters.referenceEpoch = UtilsService.julianToDateObject(updatedParameters.referenceEpoch);
      updatedParameters.objectName = this.getCopiedObjectName();
      Object.keys(updatedParameters).forEach((key) => this.setEllipticalParameter(key, updatedParameters[key]));
      this.checkDisableOrbitalFields();
    },

    getCopiedObjectName() {
      const objectsNames = this.ellipticalObjectsList().map(({objectName}) => objectName.trim().toLowerCase());
      const objectDefaultName = `${this.displayName(this.ellipticalAvailableSelected)} - copy`;
      let objectCopyNumber = 1;
      while (
          objectsNames.includes(
              objectCopyNumber === 1
                  ? objectDefaultName.toLowerCase()
                  : `${objectDefaultName} ${objectCopyNumber}`.toLowerCase()
          )
          ) {
        objectCopyNumber++;
      }
      return objectCopyNumber === 1 ? objectDefaultName : `${objectDefaultName} ${objectCopyNumber}`;
    },

    setEllipticalParameters() {
      const parameters = Object.keys(this.ellipticalInputsList);
      parameters.forEach((parameterName) => this.setEllipticalParameter(parameterName, null));

      let objectsNames = this.ellipticalObjectsList().map(({objectName}) => objectName.trim().toLowerCase());
      let objectNumber = objectsNames.length + 1;
      while (objectsNames.includes(`my object ${objectNumber}`)) {
        objectNumber++;
      }
      this.setEllipticalParameter("objectName", `my elliptical object ${objectNumber}`);
      this.ellipticalParameters["type"] = SyntheticObjectsService.OBJECT_TYPES.elliptical;
    },

    setEllipticalParameter(parameterName, parameterValue) {
      this.ellipticalParameters[parameterName] = parameterValue;
      this.validate(this.tabs.ELLIPTICAL, parameterName, parameterValue);
    },

    setHyperbolicParameters() {
      const parameters = Object.keys(this.hyperbolicInputsList);
      parameters.forEach((parameterName) => this.setHyperbolicParameter(parameterName, null));

      let objectsNames = this.hyperbolicObjectsList().map(({objectName}) => objectName.trim().toLowerCase());
      let objectNumber = objectsNames.length + 1;
      while (objectsNames.includes(`my object ${objectNumber}`)) {
        objectNumber++;
      }
      this.setHyperbolicParameter("objectName", `my hyperbolic object ${objectNumber}`);
      this.hyperbolicParameters["type"] = SyntheticObjectsService.OBJECT_TYPES.hyperbolic;
    },

    setHyperbolicParameter(parameterName, parameterValue) {
      this.hyperbolicParameters[parameterName] = parameterValue;
      this.validate(this.tabs.HYPERBOLIC, parameterName, parameterValue);
    },

    validate(type, inputId, inputValue) {
      const inputsObject = this.isEllipticalType(type) ? this.ellipticalInputsList : this.hyperbolicInputsList;
      const inputData = inputsObject && inputsObject[inputId];
      if (!inputData || !inputData.validation) {
        return;
      }
      const {validation} = inputData;
      const {schema} = validation;
      if (!schema) {
        return;
      }
      if (schema === "unique") {
        this.validateUniqueFieldValue(type, inputId, inputValue, validation);
      }
      if (schema === "limitedNumber") {
        this.validateLimitedNumber(type, inputId, inputValue, validation);
      }
      if (inputId === 'perihelionDistance' || inputId === 'aphelionDistance') {
        this.validatePerihelionAndAphelion(type);
      }
    },

    validateUniqueFieldValue(type, fieldId, fieldValue, validationData) {
      const parameters = this.isEllipticalType(type) ? this.ellipticalParameters: this.hyperbolicParameters;
      const {maxLength} = validationData;
      const objectsList = this.ellipticalObjectsList().filter((item) => !isEqual(item, this.config.selectedObject));
      const valuesList = objectsList.map((object) => object[fieldId].trim());
      fieldValue = typeof fieldValue === "string" ? fieldValue.trim() : fieldValue;
      if (maxLength && fieldValue && fieldValue.length > maxLength) {
        fieldValue = fieldValue.substring(0, maxLength);
        parameters[fieldId] = fieldValue;
      }
      if (fieldValue !== null && valuesList.includes(fieldValue)) {
        this.toggleFieldError(type, fieldId);
        return;
      }
      this.toggleFieldError(type, fieldId, false);
    },

    validateLimitedNumber(type, inputId, inputValue, validationData) {
      const parameters = this.isEllipticalType(type) ? this.ellipticalParameters: this.hyperbolicParameters;
      const {min, max, decimal} = validationData;
      if (inputValue === null) {
        this.toggleFieldError(type, inputId, false);
        return;
      }
      if (inputValue === '') {
        this.toggleFieldError(type, inputId);
        return;
      }
      if (isNaN(parseFloat(inputValue))) {
        this.toggleFieldError(type, inputId);
        return;
      }
      this.toggleFieldError(type, inputId, false);
      if (inputValue > max) {
        parameters[inputId] = max;
        return;
      }
      if (inputValue < min && (min <= 0 || min >= 1)) {
        parameters[inputId] = min;
        return;
      }
      if (+inputValue !== 0 && inputValue < min && min > 0 && min < 1) {
        parameters[inputId] = min;
        return;
      }
      if (+inputValue === 0 && min > 0 && min < 1) {
        this.toggleFieldError(type, inputId);
        return;
      }
      const valueParts = inputValue.toString().split(/[.,]/);
      const integerPart = valueParts[0];
      let decimalPart = valueParts[1] || "";
      if (!decimalPart.length) {
        parameters[inputId] = integerPart;
        return;
      }
      if (decimal && decimalPart.length > decimal) {
        decimalPart = decimalPart.slice(0, decimal);
      }
      parameters[inputId] = integerPart + '.' + decimalPart;
    },

    validatePerihelionAndAphelion(type) {
      const parameters = this.isEllipticalType(type) ? this.ellipticalParameters : this.hyperbolicParameters;
      const perihelionValue = parameters['perihelionDistance'];
      const aphelionValue = parameters['aphelionDistance'];
      if ((!perihelionValue && perihelionValue !== 0) || (!aphelionValue && aphelionValue !== 0)) {
        this.toggleFieldError(type, 'perihelionAphelion', false);
        return;
      }
      if (+aphelionValue > +perihelionValue) {
        this.toggleFieldError(type, 'perihelionAphelion', false);
        return;
      }
      this.toggleFieldError(type, 'perihelionAphelion');
    },

    toggleFieldError(type, fieldId, isError = true) {
      let errorsTab = this.isEllipticalType(type) ? this.errorsInEllipticalTab : this.errorsInHyperbolicTab;
      errorsTab[fieldId] = isError;
    },

    stopLoading(designator) {
      const loadingObjects = SynodicCalculationService.loadingObjectsEphemerides();
      loadingObjects.splice(loadingObjects.indexOf(designator), 1);
    },

    showPopupInfo(message, type = "warning") {
      PopupService.show({
        component: "PopupInfo",
        type: type,
        message: message,
      });
    },

    clearAllParameters() {
      this.resetParameters();
    },

    cancel() {
      this.$emit("cancel");
    },

    saveObject() {
      if (this.isSaveDisabled) {
        return;
      }
      if (this.isEditMode) {
        if ((this.config.objectType === this.tabs.ELLIPTICAL && this.isShowEllipticalTab) || (this.config.objectType === this.tabs.HYPERBOLIC && this.isShowHyperbolicTab)) {
          this.updateObjectData();
        } else {
          this.addNewObject()
        }
      } else {
        this.addNewObject();
      }
      this.$emit("submit");
    },

    updateObjectData() {
      const objectsList = this.getObjectsList();
      const objectData = this.getTrimmedParametersValues();

      // Old object as it was when popup opened
      const previousObject = this.config.selectedObject || {};
      const previousObjectDesignator = previousObject.designator;

      // Compute the new designator.
      objectData.designator = SyntheticObjectsService.createSyntheticObjectDesignator(objectData.objectName);

      //console.log(
      //  "updateObjectData SOVT/OVT",
      //  "oldName=", previousObject.objectName,
      //  "newName=", objectData.objectName,
      //  "oldDesignator=", previousObjectDesignator,
      //  "newDesignator=", objectData.designator
      //);

      // Find index in the synthetic objects list
      const objectIndex = objectsList.findIndex(object =>
        isEqual(object.objectName, this.config.selectedObject.objectName)
      );

      // ======== FLOATING-POINT SAFE COMPARISON ========
      
      const EPS = 1e-6;

      function nearlyEqual(a, b) {
        if (a == null && b == null) return true;
        if (a == null || b == null) return false;

        const na = Number(a);
        const nb = Number(b);

        if (isNaN(na) || isNaN(nb)) {
          // Fallback to string comparison for non-numeric fields
          return String(a) === String(b);
        }

        return Math.abs(na - nb) < EPS;
      }

      // ======== CHANGE DETECTION ========

      // 1. Name change?
      const nameChanged = objectData.objectName !== previousObject.objectName;

      // ======== 2. Orbital parameters change? (FLOAT-SAFE) ========
      let parametersChanged = false;
      let _changedParameters = [];

      Object.keys(objectData).forEach((key) => {
        // Ignore non-orbital keys
        if (key === "objectName" || key === "designator" || key === "type") {
          return;
        }

        const oldVal = previousObject[key];
        const newVal = objectData[key];

        if (!nearlyEqual(oldVal, newVal)) {
          parametersChanged = true;
          _changedParameters.push({
            param: key,
            oldValue: oldVal,
            newValue: newVal
          });
        }
      });
      //console.log(
      //  "nameChanged=", nameChanged,
      //  "parametersChanged=", parametersChanged,
      //  "currentTool=", this.currentTool
      //);
      //if (parametersChanged) {
      //  console.log("SOVT DEBUG — parameters changed:");
      //  changedParameters.forEach(p => {
      //    console.log(
      //      ` • ${p.param}: old="${p.oldValue}" -> new="${p.newValue}"`
      //    );
      //  });
      //}
      // ======================================================
      // SPECIAL CASE: OVT + ONLY NAME CHANGED
      // ======================================================
      if (this.currentTool === "ovt" && nameChanged && !parametersChanged) {
        // We only rename the object, do NOT reload orbit or change designator.

        // 1) Keep OLD designator so all OVT internal lists still match.
        objectData.designator = previousObjectDesignator;

        // 2) Update the object in the synthetic objects list (name only)
        objectsList[objectIndex] = {
          ...objectsList[objectIndex],
          objectName: objectData.objectName,
          designator: previousObjectDesignator,
        };
        this.updateObjectsList(objectsList);

        // 3) Update the plotted OVT object(s) label in the 3D scene
        const vt = window["vt"];
        if (vt && Array.isArray(vt.spaceObjects)) {
          const idsToUpdate = [
            previousObjectDesignator,                 // keplerian synthetic
            `${previousObjectDesignator}_perturbed`,  // perturbed synthetic (if exists)
          ];

          vt.spaceObjects.forEach((obj) => {
            if (!idsToUpdate.includes(obj._id)) return;

            // Update Spacekit options name
            if (obj._options) {
              obj._options.name = objectData.objectName;
            }

            // Keep 3D object name in sync (useful for debugging / picking)
            if (obj._object3js) {
              obj._object3js.name = objectData.objectName;
            }

            // Update existing HUD label text without recreating orbits
            if (obj._label3d && obj._label3d[0]) {
              const label = obj._label3d[0];
              label.text = objectData.objectName;
              label.name = objectData.objectName;
              if (typeof label.sync === "function") {
                label.sync();
              }
            }
          });
        }

        // 4) Update SOVT cached synodic name so when switching back to SOVT
        //    the label is recreated with the new name.
        const calculatedList = SynodicCalculationService.getCalculatedRealSynodicObjectsList();
        if (Array.isArray(calculatedList) && calculatedList.length) {
          const entry = calculatedList.find(
            (item) => item.designator === previousObjectDesignator
          );
          if (entry) {
            if (entry.objectData) {
              entry.objectData.objectName = objectData.objectName;
            }
            if (entry.objectEphemData) {
              entry.objectEphemData.objectName = objectData.objectName;
            }
          }
        }
        const orbitActiveObject = VisualisationService.getOrbitActiveObject()
        if (orbitActiveObject) {
          VisualisationService.setOrbitActiveObject(orbitActiveObject)
          this.$forceUpdate();
        }

        return;
      }
      // ======== SPECIAL CASE: SOVT + ONLY NAME CHANGED ========
      if (this.currentTool === "sovt" && nameChanged && !parametersChanged) {
        // We only rename the object. The orbit and synodic state must stay as is.

        // 1) Keep the OLD designator so SOVT internal synodic lists still match.
        objectData.designator = previousObjectDesignator;

        // 2) Update the object in the synthetic objects list (name only)
        objectsList[objectIndex] = {
          ...objectsList[objectIndex],
          objectName: objectData.objectName,
          designator: previousObjectDesignator,
        };
        this.updateObjectsList(objectsList);

        // 3) Update ALL synodic space-object labels for this designator
        //    (both Keplerian and perturbed), regardless of which one is active.
        const vt = window["vt"];
        if (vt && Array.isArray(vt.spaceObjects)) {
          [false, true].forEach((isPerturbed) => {
            const spaceObjectId =
              SynodicCalculationService.getTransformedSpaceObjectId(
                previousObjectDesignator,
                isPerturbed
              );
            const spaceObject = vt.spaceObjects.find(
              (obj) => obj._id === spaceObjectId
            );
            if (!spaceObject) return;

            // Update Spacekit options name
            if (spaceObject._options) {
              spaceObject._options.name = objectData.objectName;
            }

            // Keep 3D object name in sync (useful for debugging / picking)
            if (spaceObject._object3js) {
              spaceObject._object3js.name = objectData.objectName;
            }

            // Update existing HUD label text without recreating orbits
            if (spaceObject._label3d && spaceObject._label3d[0]) {
              const label = spaceObject._label3d[0];
              label.text = objectData.objectName;
              label.name = objectData.objectName;
              //console.log("SOVT: updated label text to", label.text);
              if (typeof label.sync === "function") {
                label.sync();
              }
            }
          });
        }
        // 4) Also update the stored calculated synodic object name,
        //    so future toggles (Keplerian <-> Perturbed) use the new name
        const calculatedList =
          SynodicCalculationService.getCalculatedRealSynodicObjectsList();

        if (Array.isArray(calculatedList) && calculatedList.length) {
          const entry = calculatedList.find(
            (item) => item.designator === previousObjectDesignator
          );
          if (entry && entry.objectData) {
            entry.objectData.objectName = objectData.objectName;
            //console.log(
            //  "SOVT: updated calculatedRealSynodicObjectsList.objectData.objectName to",
            //  objectData.objectName
            //);
          }
        }
        // We keep the existing orbit and perturbed/Keplerian mode.
        let synodicActiveObject = SynodicCalculationService.getSynodicActiveObject();
        if (synodicActiveObject) {
          // Update options name
          if (synodicActiveObject._options) {
            synodicActiveObject._options.name = objectData.objectName;
          }
          // Keep 3D object name in sync (useful for debugging / picking)
          if (synodicActiveObject._object3js) {
            synodicActiveObject._object3js.name = objectData.objectName;
          }
          // Update existing HUD label text without recreating orbits
          if (synodicActiveObject._label3d && synodicActiveObject._label3d[0]) {
            let label = synodicActiveObject._label3d[0];
            label.text = objectData.objectName;
            label.name = objectData.objectName;
            //console.log("SOVT: updated label text to", label.text);
            if (typeof label.sync === "function") {
              label.sync();
            }
          }
          SynodicCalculationService.setSynodicActiveObject(synodicActiveObject);
          this.$forceUpdate();
        }
        return;
      }

      // ======== DEFAULT BEHAVIOR (OVT, or SOVT when parameters changed) ========
      // For these cases we keep your original designator logic and calls.

      objectsList[objectIndex] = objectData;
      this.updateObjectsList(objectsList);

      if (this.currentTool === "ovt") {
        this.adjustOvtToolFeatures(objectData.designator, previousObjectDesignator);
      }
      if (this.currentTool === "sovt") {
        this.adjustSovtToolFeatures(objectData.designator, previousObjectDesignator);
      }
    },

    adjustOvtToolFeatures(currentDesignator, previousObjectDesignator) {
      const {
        checkedObjectsList,
        perturbedObjectsList,
        isObjectChecked,
        isObjectSelected,
        isObjectPerturbed
      } = SyntheticObjectsService.getOvtObjectsInformationsByDesignator(previousObjectDesignator);
      isObjectPerturbed && this.adjustOvtPerturbedObjects(perturbedObjectsList, previousObjectDesignator, currentDesignator);
      isObjectSelected && this.adjustOvtSelectedObject(currentDesignator);
      isObjectChecked && this.adjustOvtCheckedObjects(checkedObjectsList, previousObjectDesignator, currentDesignator, isObjectPerturbed);
    },

    adjustOvtPerturbedObjects(perturbedObjectsList, previousObjectDesignator, designator) {
      perturbedObjectsList[perturbedObjectsList.indexOf(previousObjectDesignator)] = designator;
    },

    adjustOvtSelectedObject(designator) {
      VisualisationService.setSelectedObjectName(designator);
    },

    adjustOvtCheckedObjects(checkedObjectsList, previousObjectDesignator, designator, isObjectPerturbed) {
      const focusedObject = VisualisationService.getOvtFocusedObject();
      const isFocusedUpdated = focusedObject === previousObjectDesignator;
      isFocusedUpdated && VisualisationService.setOvtFocusedObject('');
      checkedObjectsList[checkedObjectsList.indexOf(previousObjectDesignator)] = designator;
      VisualisationService.removeObject(`${previousObjectDesignator}${isObjectPerturbed ? "_perturbed" : ""}`);
      SpacekitService.removeMarkerByName('marker_' + `${previousObjectDesignator}${isObjectPerturbed ? "_perturbed" : ""}`);
      VisualisationService.addObject(designator, isObjectPerturbed, this.isShowEllipticalTab ? SyntheticObjectsService.OBJECT_TYPES.elliptical : SyntheticObjectsService.OBJECT_TYPES.hyperbolic);
      isFocusedUpdated && VisualisationService.setOvtFocusedObject(designator);
    },

    adjustSovtToolFeatures(currentDesignator, previousObjectDesignator) {
      const {
        calculatedObjectList,
        calculatedObjectIndex,
        perturbedObjectsList,
        perturbedObjectIndex,
        isObjectSelected,
        isObjectVisible,
        objectIndexInVisibleList,
      } = this.getSovtObjectsInformation(previousObjectDesignator);
      const isPerturbedObjectOrbit = perturbedObjectIndex !== -1;
      if (isPerturbedObjectOrbit) {
        perturbedObjectsList[perturbedObjectIndex] = currentDesignator;
        SynodicCalculationService.setPerturbedSynodicOrbitList(perturbedObjectsList);
      }
      if (calculatedObjectIndex === -1) {
        isObjectSelected && VisualisationService.selectSynodicObject(currentDesignator);
        return;
      }
      this.adjustSovtCalculatedObject(
          calculatedObjectList,
          calculatedObjectIndex,
          currentDesignator,
          previousObjectDesignator,
          isObjectVisible,
          objectIndexInVisibleList,
          isObjectSelected,
          isPerturbedObjectOrbit
      );
    },

    getSovtObjectsInformation(previousObjectDesignator) {
      const calculatedObjectList = UtilsService.deepCopy(
          SynodicCalculationService.getCalculatedRealSynodicObjectsList()
      );
      const calculatedObjectIndex = UtilsService.findItemIndexInObjectList(
          "designator",
          previousObjectDesignator,
          calculatedObjectList
      );
      const perturbedObjectsList = UtilsService.deepCopy(
          SynodicCalculationService.getPerturbedSynodicOrbitList()
      );
      const perturbedObjectIndex = perturbedObjectsList.indexOf(previousObjectDesignator);
      const {isObjectSelected, isObjectVisible, objectIndexInVisibleList} =
          SynodicCalculationService.getSynodicObjectVisibilityInfo(previousObjectDesignator);
      return {
        calculatedObjectList,
        calculatedObjectIndex,
        perturbedObjectsList,
        perturbedObjectIndex,
        isObjectSelected,
        isObjectVisible,
        objectIndexInVisibleList,
      };
    },

    adjustSovtCalculatedObject(
        calculatedObjectList,
        calculatedObjectIndex,
        currentDesignator,
        previousObjectDesignator,
        isObjectVisible,
        objectIndexInVisibleList,
        isObjectSelected,
        isPerturbedObjectOrbit
    ) {
      calculatedObjectList[calculatedObjectIndex].designator = currentDesignator;
      calculatedObjectList[calculatedObjectIndex].objectData = SyntheticObjectsService.getSyntheticObjectParameters(currentDesignator, this.isShowEllipticalTab ? SyntheticObjectsService.OBJECT_TYPES.elliptical : SyntheticObjectsService.OBJECT_TYPES.hyperbolic);
      calculatedObjectList[calculatedObjectIndex].objectData.catalogueNumber = currentDesignator;
      SynodicCalculationService.setCalculatedRealSynodicObjectsList(calculatedObjectList);

      if (isObjectVisible) {
        const focusedObject = VisualisationService.getSovtFocusedObject();
        const isFocusedUpdated = focusedObject === previousObjectDesignator;
        isFocusedUpdated && VisualisationService.setOvtFocusedObject('');
        const visibleRealSynodicObjectsList = SynodicCalculationService.getVisibleRealSynodicObjectsList();
        visibleRealSynodicObjectsList.splice(objectIndexInVisibleList, 1);
        SynodicCalculationService.setVisibleRealSynodicObjectsList(visibleRealSynodicObjectsList);
        SynodicCalculationService.removeExistingSpaceObject(previousObjectDesignator, isPerturbedObjectOrbit);
        {
          const cachedPerturbedData = SynodicCalculationService.getSynodicPerturbedData();
          const updatedPerturbedData = Array.isArray(cachedPerturbedData)
            ? cachedPerturbedData.filter(
                obj =>
                  obj.designator !== previousObjectDesignator &&
                  obj.designator !== currentDesignator
              )
            : [];
          SynodicCalculationService.setSynodicPerturbedData(updatedPerturbedData);
        }
        SynodicChartService.removeSynodicOrbitObjectChartData(previousObjectDesignator);
        SynodicCalculationService.showCalculatedInvisibleObject(currentDesignator);
        if (isObjectSelected) {
          VisualisationService.setSelectedSynodicObjectName(currentDesignator);
        }
        isFocusedUpdated && VisualisationService.setSovtFocusedObject(currentDesignator);
      }      
    },

    addNewObject() {
      const objectsList = this.getObjectsList();
      const objectData = this.getTrimmedParametersValues();
      objectData.designator = SyntheticObjectsService.createSyntheticObjectDesignator(objectData.objectName);
      objectsList.push(objectData);
      this.updateObjectsList(objectsList);
    },

    getTrimmedParametersValues() {
      const updatedParameters = {};
      if (this.isShowEllipticalTab) {
        Object.keys(this.ellipticalParameters).forEach((parameterKey) => {
          const parameterValue = this.ellipticalParameters[parameterKey];
          updatedParameters[parameterKey] = typeof parameterValue === "string" ? parameterValue.trim() : parameterValue;
          if (this.ellipticalInputsList[parameterKey] && this.ellipticalInputsList[parameterKey].type === 'number') {
            updatedParameters[parameterKey] = parseFloat(updatedParameters[parameterKey]);
          }

        });
      }
      if (this.isShowHyperbolicTab) {
        Object.keys(this.hyperbolicParameters).forEach((parameterKey) => {
          const parameterValue = this.hyperbolicParameters[parameterKey];
          updatedParameters[parameterKey] = typeof parameterValue === "string" ? parameterValue.trim() : parameterValue;
          if (this.hyperbolicInputsList[parameterKey] && this.hyperbolicInputsList[parameterKey].type === 'number') {
            updatedParameters[parameterKey] = parseFloat(updatedParameters[parameterKey]);
          }
        });
      }
      if (updatedParameters.referenceEpoch) {
        updatedParameters.referenceEpoch = (this.dateToJulian(new Date(updatedParameters.referenceEpoch)) - 2400000.5).toFixed(8) * 1;
      }
      if (updatedParameters.pericentrePassage) {
        updatedParameters.pericentrePassage = (this.dateToJulian(new Date(updatedParameters.pericentrePassage)) - 2400000.5).toFixed(8) * 1;
      }
      return updatedParameters;
    },

    dateToJulian(date) {
      // extracted from julian-date library because of missing roundings
      return (new Date(new moment.utc(date)).valueOf() / (1000 * 60 * 60 * 24)).toFixed(8) - 0.5 + 2440588;
    },

    updateObjectsList(objectsList) {
      this.isShowEllipticalTab
          ? SyntheticObjectsService.setEllipticalObjectsList(objectsList)
          : SyntheticObjectsService.setHyperbolicObjectsList(objectsList);
    },

    getObjectsList() {
      return this.isShowEllipticalTab
          ? [...SyntheticObjectsService.getEllipticalObjectsList()]
          : [...SyntheticObjectsService.getHyperbolicObjectsList()];
    },

    ellipticalObjectsList() {
      return [...SyntheticObjectsService.getEllipticalObjectsList()]
    },

    hyperbolicObjectsList() {
      return [...SyntheticObjectsService.getHyperbolicObjectsList()]
    },
  },
  created() {
    this.setParameters();
  },
  mounted() {
    if (this.isEditMode) {
      this.changeMode(this.config.objectType);
      this.fillEditedObjectParameters();
    } else {
      this.changeMode(this.tabs.ELLIPTICAL);
    }
    ObjectsService.setAvailableAsteroidsList([]);
    ObjectsService.setAvailableAsteroidsTotal(0);
    ObjectsService.setAvailableAsteroidsHasMore(false);
    ObjectsService.resetAvailableAsteroidsSearchSession();
    this.$refs.objectName[0].$refs.objectName.focus();
  },
  beforeUnmount() {
    clearTimeout(this.typingDelay);
  },
};
</script>

<style lang="scss">
@import "@/styles/common/popup-synthetic-object-configuration";
@import "@/styles/common/popup-objects-configuration";
</style>
